import { Component, Input, OnInit } from '@angular/core';
import {
  ProFormQuestionAnswerOption,
  ProformViewStates,
} from '../../../dtos/proforms.dto';
import { ProformsSubmissionService } from '../../../services/proforms/proforms-submission.service';
import { CreateProformSubmissionPayload } from '../../../types/payloads/proforms.payloads';
import {
  ProformFieldTypes,
  ProFormsAnswerOptionAPIResponse,
  ProFormsQuestionAPIResponse,
  ProFormsSubmissionAPIResponse,
} from '../../../types/responses/proforms.responses';

@Component({
  selector: 'app-proforms-question-answer-options',
  standalone: false,
  templateUrl: './proforms-question-answer-options.component.html',
  styleUrl: './proforms-question-answer-options.component.scss',
})
export class ProformsQuestionAnswerOptionsComponent implements OnInit {
  @Input() question: ProFormsQuestionAPIResponse;

  @Input() viewState: ProformViewStates = ProformViewStates.PREVIEW;

  @Input() showAnswers = 0;

  answerOptionIds: number[] = [];

  selectedOption: ProFormsAnswerOptionAPIResponse | null = null;

  FieldTypes = ProformFieldTypes;

  viewStates = ProformViewStates;

  hasCorrectAnswers = false;

  constructor(private proformsSubmissionService: ProformsSubmissionService) {}

  ngOnInit(): void {
    if (this.question) {
      if (
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED
      ) {
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            this.setAnswerOptionIdsFromSubmission(submission);
          }
        );
      } else if (this.viewState === this.viewStates.EDIT) {
        this.proformsSubmissionService.newSubmission.subscribe((submission) => {
          this.setAnswerOptionIdsFromSubmission(submission);
        });
      }

      this.hasCorrectAnswers = this.question.answerOptions.some(
        (option) => option.is_correct
      );
    }
  }

  get isInputInteractive(): boolean {
    return this.viewState === this.viewStates.EDIT;
  }

  setAnswerOptionIdsFromSubmission(
    submission:
      | CreateProformSubmissionPayload
      | ProFormsSubmissionAPIResponse
      | null
  ): void {
    if (!submission?.answers) {
      this.answerOptionIds = [];
      this.selectedOption = null;
      return;
    }

    const questionAnswers = submission.answers.filter(
      (a) => a.proform_form_question_id === this.question?.id
    );

    if (questionAnswers.length === 0) {
      this.answerOptionIds = [];
      this.selectedOption = null;
      return;
    }

    if (this.question?.field_type === this.FieldTypes.CHECKBOX) {
      this.answerOptionIds = questionAnswers
        .map((a) => a.proform_form_question_answer_option_id)
        .filter((id): id is number => id !== undefined);
    } else {
      const firstAnswer = questionAnswers[0];
      this.answerOptionIds = firstAnswer.proform_form_question_answer_option_id
        ? [firstAnswer.proform_form_question_answer_option_id]
        : [];

      // Set the selected option for dropdown fields
      if (
        this.question?.field_type === this.FieldTypes.DROPDOWN &&
        firstAnswer.proform_form_question_answer_option_id
      ) {
        this.selectedOption =
          this.question.answerOptions.find(
            (option) =>
              option.id === firstAnswer.proform_form_question_answer_option_id
          ) || null;
      }
    }
  }

  onOptionChange(optionId: number, event: Event): void {
    const inputElement = event.target as HTMLInputElement;

    const isChecked = inputElement.checked;

    if (isChecked) {
      this.proformsSubmissionService.addAnswerToPayload(
        {
          proform_form_question_id: this.question.id,
          proform_form_question_answer_option_id: optionId,
        },
        this.question.field_type
      );
    } else {
      this.proformsSubmissionService.removeAnswerFromPayload(optionId);
    }
  }

  handleDropdownSelect(answer: ProFormQuestionAnswerOption) {
    this.selectedOption = answer;
    this.proformsSubmissionService.addAnswerToPayload(
      {
        proform_form_question_id: this.question.id,
        proform_form_question_answer_option_id: answer.id,
      },
      this.question.field_type
    );
  }
}
